import {
  Editor,
  Element,
  type ElementEntry,
  type PathRef,
  Range,
  Transforms,
} from 'slate';
import {
  getDirectParentList,
  getSelectedListItems,
  getParentList,
} from './utils';
import { isList, SUPPORTED_TYPES } from './guards';
import ListDepthUtils from './ListDepthUtils';
import { type ListItemElement } from '../../types/aha-editor';

interface WrapInListOptions {
  type: 'ol_list' | 'ul_list' | 'dl_list' | 'checklist';
  listData?: object;
  itemData?: object;
}

export class ListWrapUtils {
  static wrapInList(editor: Editor, options: WrapInListOptions): void {
    Editor.withoutNormalizing(editor, () => {
      if (!Object.hasOwn(options, 'type')) {
        throw new Error('A list type must be specified.');
      }

      const { type, listData = {}, itemData = {} } = options;
      if (!SUPPORTED_TYPES.includes(type)) {
        throw new Error(
          `Type ${type} is not supported, it must be one of ${SUPPORTED_TYPES.join(
            ','
          )}.`
        );
      }

      // If multiple list items are selected, change the type of all
      // lists in the selection
      if (editor.selection && Range.isExpanded(editor.selection)) {
        const selectedListItems = getSelectedListItems(editor);
        if (selectedListItems.length > 0) {
          for (const [, path] of selectedListItems) {
            if (!path.current) continue;

            const parentListEntry = getParentList(editor, path.current);
            if (parentListEntry) {
              const [parentList, parentListPath] = parentListEntry;

              // Only change the type if it's different
              if (parentList.type !== type) {
                Transforms.setNodes(editor, { type }, { at: parentListPath });
              }
            }
          }
          return;
        }
      }

      // If we're inside a list, change the type of the current list
      const parentListEntry = getDirectParentList(editor);
      if (parentListEntry) {
        const [, parentListPath] = parentListEntry;
        Transforms.setNodes(editor, { type }, { at: parentListPath });
        return;
      }

      // Otherwise, wrap the next block above the current selection.
      const blocks: [Element, PathRef][] = Array.from(
        Editor.nodes(editor, {
          match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
          mode: 'lowest',
        })
      ).map(([element, path]: ElementEntry): [Element, PathRef] => [
        element,
        Editor.pathRef(editor, path),
      ]);

      Transforms.wrapNodes(
        editor,
        { type, ...listData, children: [] },
        editor.selection ? { at: editor.selection } : undefined
      );

      for (const [element, path] of blocks) {
        if (!path.current) continue;

        if (isList(element)) {
          Transforms.unwrapNodes(editor, { at: path.current });
        } else {
          const listItem: ListItemElement = {
            type: 'list_item',
            ...itemData,
            children: [],
          };
          if (type === 'checklist') {
            listItem.list_checked = false;
          }

          Transforms.wrapNodes(editor, listItem, { at: path.current });
        }
      }
    });
  }

  static unwrapList(editor: Editor): void {
    const blocks = getSelectedListItems(editor);

    for (const [element, pathRef] of blocks) {
      const path = pathRef.unref();
      if (!path) continue;

      ListDepthUtils.decreaseItemDepth(editor, [element, path]);
    }
  }
}

export default ListWrapUtils;
